/**
 * CameraControls - PlayCanvas Camera Controller
 *
 * Ported from PlayCanvas engine scripts/esm/camera-controls.mjs
 * Provides orbit, fly, and focus camera modes with mouse, touch, and gamepad support.
 *
 * Controls:
 * - LMB drag: Orbit around focus point
 * - RMB drag + WASD: Fly mode
 * - Shift + drag / MMB: Pan
 * - Scroll / Pinch: Zoom
 * - Double-click: Focus on point (handled externally)
 */

import * as pc from 'playcanvas';
import { VoxelCollision } from './VoxelCollision';
import { applyLookInversion } from './lookInversion';
import { readGamepad, GP_BUTTON } from './gamepadUtils';
import { fileUnitsPerWorldUnit } from './splatCollisionSpace';
import { triangleColliderOf } from './customMeshCollider';
import { colliderBox, type ColliderBox } from './colliderBox';

type CameraComponentWithSystem = Omit<pc.CameraComponent, 'system'> & {
  system: {
    app?: pc.Application;
  };
};

type FocusControllerWithComplete = Omit<pc.FocusController, 'complete'> & {
  complete?: () => boolean;
};

type AppWithXR = Omit<pc.Application, 'xr'> & {
  xr?: {
    active?: boolean;
  };
};

const tmpV1 = new pc.Vec3();
const tmpV2 = new pc.Vec3();
// Dedicated out-scratches for the two screenToWorld calls in update(). MUST NOT be tmpV1/tmpV2:
// screenToWorld clobbers tmpV2 internally (halfSize) and tmpV1 is live as `v` at the call sites.
const tmpV3 = new pc.Vec3();
const tmpV4 = new pc.Vec3();
const tmpQ1 = new pc.Quat();
const pose = new pc.Pose();
const frame = new pc.InputFrame({
  move: [0, 0, 0],
  rotate: [0, 0, 0]
});

const isEditableElement = (element: Element | null): boolean => {
  if (typeof HTMLElement === 'undefined') return false;
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;

  const tagName = element.tagName;
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return true;
  }

  return element.closest('[contenteditable="true"], [role="textbox"]') !== null;
};

/**
 * Calculate the damp rate for smooth interpolation
 */
export const damp = (damping: number, dt: number): number => 1 - Math.pow(damping, dt * 1000);

/**
 * Apply dead zone to gamepad stick input
 */
const applyDeadZone = (stick: number[], low: number, high: number): void => {
  const mag = Math.sqrt(stick[0] * stick[0] + stick[1] * stick[1]);
  if (mag < low) {
    stick.fill(0);
    return;
  }
  const scale = (mag - low) / (high - low);
  stick[0] *= scale / mag;
  stick[1] *= scale / mag;
};

/**
 * Converts screen space mouse deltas to world space pan vector
 */
const screenToWorld = (
  camera: pc.CameraComponent,
  dx: number,
  dy: number,
  dz: number,
  out: pc.Vec3 = new pc.Vec3()
): pc.Vec3 => {
  const { fov, aspectRatio, horizontalFov, projection, orthoHeight } = camera;
  const app = (camera as unknown as CameraComponentWithSystem).system?.app;
  const { width, height } = app?.graphicsDevice?.clientRect || { width: 1920, height: 1080 };

  // normalize deltas to device coord space
  out.set(
    -(dx / width) * 2,
    (dy / height) * 2,
    0
  );

  // calculate half size of the view frustum at the current distance
  const halfSize = tmpV2.set(0, 0, 0);
  if (projection === pc.PROJECTION_PERSPECTIVE) {
    const halfSlice = dz * Math.tan(0.5 * fov * pc.math.DEG_TO_RAD);
    if (horizontalFov) {
      halfSize.set(halfSlice, halfSlice / aspectRatio, 0);
    } else {
      halfSize.set(halfSlice * aspectRatio, halfSlice, 0);
    }
  } else {
    halfSize.set(orthoHeight * aspectRatio, orthoHeight, 0);
  }

  // scale by device coord space
  out.mul(halfSize);
  return out;
};

type CameraMode = 'orbit' | 'fly' | 'focus';
type MobileInputLayout = 'joystick-joystick' | 'joystick-touch' | 'touch-joystick' | 'touch-touch';

/** Options for animateToOrbitView / animateToFlyView. */
export interface ViewMoveOptions {
  /** Land on the next frame instead of travelling (a hotspot's "Instant" teleport mode). */
  instant?: boolean;
}
/** An "instant" view move still runs through the tween so it ends (and calls back) the same way. */
const INSTANT_VIEW_MOVE_SECONDS = 1e-4;

export interface OrbitRotationLimitsConfig {
  enabled?: boolean;
  up?: number;
  down?: number;
  left?: number;
  right?: number;
}

export interface CameraControlsConfig {
  enableOrbit?: boolean;
  enableFly?: boolean;
  enablePan?: boolean;
  focusPoint?: pc.Vec3;
  moveSpeed?: number;
  moveFastSpeed?: number;
  moveSlowSpeed?: number;
  rotateSpeed?: number;
  rotateTouchSens?: number;
  rotateJoystickSens?: number;
  zoomSpeed?: number;
  zoomPinchSens?: number;
  focusDamping?: number;
  rotateDamping?: number;
  moveDamping?: number;
  zoomDamping?: number;
  pitchRange?: pc.Vec2;
  yawRange?: pc.Vec2;
  zoomRange?: pc.Vec2;
  gamepadDeadZone?: pc.Vec2;
  mobileInputLayout?: MobileInputLayout;
  /**
   * Invert drag-to-look on BOTH axes (scene setting `invertCameraRotation`, editor "Invert Drag
   * Direction"): drag the scene instead of the view. Applies to mouse and touch drags in orbit and
   * fly; gamepad sticks keep the conventional invert-Y meaning. See lookInversion.ts.
   */
  invertRotation?: boolean;
  /** Optional yaw/pitch limits in degrees, relative to the initial orbit view */
  orbitRotationLimits?: OrbitRotationLimitsConfig | null;
}

/**
 * CameraControls class - Manages camera with orbit, fly, and focus modes
 */
export class CameraControls {
  private camera: pc.Entity;
  private cameraComponent: pc.CameraComponent;
  private app: pc.Application;
  private enabled: boolean = true;

  // Mode state
  private _mode: CameraMode = 'orbit';
  private _enableOrbit: boolean = true;
  private _enableFly: boolean = true;
  enablePan: boolean = true;

  // Controllers
  private _flyController: pc.FlyController;
  private _orbitController: pc.OrbitController;
  private _focusController: pc.FocusController;
  private _controller: pc.InputController;
  private _pose: pc.Pose = new pc.Pose();
  private _preFocusMode: CameraMode = 'orbit';

  // Input sources
  private _desktopInput: pc.KeyboardMouseSource;
  private _orbitMobileInput: pc.MultiTouchSource;
  private _flyMobileInput: pc.DualGestureSource;
  private _gamepadInput: pc.GamepadSource;
  private _inputsDetached: boolean = false;

  // Zoom
  private _startZoomDist: number = 0;
  // Limit pitch to ±89° to avoid gimbal lock (world flip at ±90°)
  private _pitchRange: pc.Vec2 = new pc.Vec2(-89, 89);
  private _yawRange: pc.Vec2 = new pc.Vec2(-Infinity, Infinity);

  // Store the last focus point for orbit mode
  private _lastFocusPoint: pc.Vec3 = new pc.Vec3(0, 0, 0);
  private _zoomRange: pc.Vec2 = new pc.Vec2(0.01, Infinity);
  private _orbitRotationLimits: Required<OrbitRotationLimitsConfig> | null = null;
  private _orbitRotationLimitAnchor: { pitch: number; yaw: number; focus: pc.Vec3 } | null = null;

  // Debug: track last yaw for discontinuity detection
  private _lastYaw?: number;

  // State tracking
  private _state = {
    axis: new pc.Vec3(),
    shift: 0,
    ctrl: 0,
    mouse: [0, 0, 0],
    touches: 0
  };

  // Speed settings
  moveSpeed: number = 25;
  moveFastSpeed: number = 50;
  moveSlowSpeed: number = 10;
  rotateSpeed: number = 0.05;
  rotateTouchSens: number = 1.0;  // Touch sensitivity (matches desktop)
  rotateJoystickSens: number = 1;
  zoomSpeed: number = 0.001;
  zoomPinchSens: number = 5;
  keyboardSpeedMultiplier: number = 1.5;  // Desktop keyboard moves faster than mobile joystick
  gamepadDeadZone: pc.Vec2 = new pc.Vec2(0.3, 0.6);
  /** Invert drag-to-look on both axes (see CameraControlsConfig.invertRotation) */
  invertRotation: boolean = false;
  autoMoveForward: boolean = false;
  autoMoveSpeedFactor: number = 1.0;

  // Joystick event name for UI
  joystickEventName: string = 'joystick';

  // Collision detection for fly mode
  private _collisionEntities: pc.Entity[] = [];
  private _collisionRadius: number = 0.3;
  private readonly _collisionBox: ColliderBox = { cx: 0, cy: 0, cz: 0, hx: 0, hy: 0, hz: 0 };
  private _prevPosition: pc.Vec3 = new pc.Vec3();
  private _prevPositionValid: boolean = false;
  private _collisionTestVec: pc.Vec3 = new pc.Vec3();
  // Orbit-mode rollback state: last non-colliding pose snapshot
  private _prevPose: pc.Pose = new pc.Pose();
  private _prevPoseValid: boolean = false;

  // Voxel octree collision for fly/orbit modes
  private _voxelCollision: VoxelCollision | null = null;
  private _meshCollision: import('./MeshCollision').MeshCollision | null = null;
  // Which splat-derived collision representation is queried in fly/orbit mode.
  // Defaults to 'voxel' to match the persisted scene default; flipped via
  // setCollisionSystem(). Custom collision meshes (primitives/uploaded GLBs) are
  // additive — they always run regardless of this selector.
  private _collisionSystem: 'voxel' | 'mesh' = 'voxel';
  // Master toggle — when false, voxel + mesh queries are skipped (primitives still apply).
  private _splatCollisionEnabled: boolean = true;
  private _voxelSplatEntity: pc.Entity | null = null;
  private _voxelSplatMatrix: pc.Mat4 = new pc.Mat4();
  private _voxelInvSplatMatrix: pc.Mat4 = new pc.Mat4();
  private _voxelFilePos: pc.Vec3 = new pc.Vec3();

  // Manual fly-to animation (bypasses FocusController's root/child decomposition)
  private _flyToActive: boolean = false;
  private _flyToIsFocus: boolean = false; // true when tween is for orbit refocus (vs fly-to)
  private _flyToStartPos: pc.Vec3 = new pc.Vec3();
  private _flyToEndPos: pc.Vec3 = new pc.Vec3();
  private _flyToStartAngles: pc.Vec3 = new pc.Vec3();
  private _flyToEndAngles: pc.Vec3 = new pc.Vec3();
  private _flyToProgress: number = 0;
  private _flyToDuration: number = 0.6; // seconds
  // animateToOrbitView: rotation limits lifted for the move (re-anchored on arrival) and the
  // caller's callback for when it ends.
  private _flyToRestoreLimits: Required<OrbitRotationLimitsConfig> | null = null;
  private _flyToOnDone: (() => void) | null = null;
  // True for the length of the move: a click-to-refocus or fly-to then can't take over the tween
  // (it would re-anchor the restored limits around its own point and delay onDone).
  private _orbitViewMoveActive = false;

  // Cleanup handlers
  private _destroyed = false;

  constructor(camera: pc.Entity, app: pc.Application, config: CameraControlsConfig = {}) {
    this.camera = camera;
    this.app = app;

    const cameraComponent = camera.camera;
    if (!cameraComponent) {
      throw new Error('CameraControls: camera component not found on entity');
    }
    this.cameraComponent = cameraComponent;

    // Initialize controllers
    this._flyController = new pc.FlyController();
    this._orbitController = new pc.OrbitController();
    this._focusController = new pc.FocusController();

    // Set default dampening values for responsive BabylonJS-like feel
    // Lower values = more responsive, less "floaty" (0.75 matches BabylonJS snappiness better than 0.9)
    this._flyController.moveDamping = 0.75;
    this._flyController.rotateDamping = 0.75;
    this._orbitController.rotateDamping = 0.75;
    this._orbitController.zoomDamping = 0.8;

    // Set orbit controller defaults
    this._orbitController.zoomRange = new pc.Vec2(0.01, Infinity);

    // Initialize input sources
    const canvas = app.graphicsDevice.canvas;
    this._desktopInput = new pc.KeyboardMouseSource();
    this._orbitMobileInput = new pc.MultiTouchSource();
    this._flyMobileInput = new pc.DualGestureSource();
    this._gamepadInput = new pc.GamepadSource();

    // Attach input sources to canvas
    // Only attach the mobile input matching the initial mode (orbit).
    // Attaching both MultiTouchSource and DualGestureSource simultaneously
    // causes them to fight over setPointerCapture(), breaking pinch-to-zoom.
    this._desktopInput.attach(canvas);
    this._orbitMobileInput.attach(canvas);
    // _flyMobileInput left detached — will be attached when switching to fly mode
    this._gamepadInput.attach(canvas);

    // Expose UI joystick events
    this._flyMobileInput.on('joystick:position:left', ([bx, by, sx, sy]: number[]) => {
      // Always fire release events (bx < 0) to prevent stuck joystick UI
      // Also fire when disabled (walk mode uses joystick via CharacterController)
      if (bx < 0 || this._mode === 'fly' || !this.enabled) {
        this.app.fire(`${this.joystickEventName}:left`, bx, by, sx, sy);
      }
    });
    this._flyMobileInput.on('joystick:position:right', ([bx, by, sx, sy]: number[]) => {
      if (bx < 0 || this._mode === 'fly' || !this.enabled) {
        this.app.fire(`${this.joystickEventName}:right`, bx, by, sx, sy);
      }
    });

    // Initialize pose from camera position
    this._pose.look(this.camera.getPosition(), pc.Vec3.ZERO);

    // Set initial mode
    this._setMode('orbit');

    // Store controller reference
    this._controller = this._orbitController;

    // Apply config
    if (config.enableOrbit !== undefined) this.enableOrbit = config.enableOrbit;
    if (config.enableFly !== undefined) this.enableFly = config.enableFly;
    if (config.enablePan !== undefined) this.enablePan = config.enablePan;
    if (config.focusPoint) this.focusPoint = config.focusPoint;
    if (config.moveSpeed !== undefined) this.moveSpeed = config.moveSpeed;
    if (config.moveFastSpeed !== undefined) this.moveFastSpeed = config.moveFastSpeed;
    if (config.moveSlowSpeed !== undefined) this.moveSlowSpeed = config.moveSlowSpeed;
    if (config.rotateSpeed !== undefined) this.rotateSpeed = config.rotateSpeed;
    if (config.rotateTouchSens !== undefined) this.rotateTouchSens = config.rotateTouchSens;
    if (config.rotateJoystickSens !== undefined) this.rotateJoystickSens = config.rotateJoystickSens;
    if (config.zoomSpeed !== undefined) this.zoomSpeed = config.zoomSpeed;
    if (config.zoomPinchSens !== undefined) this.zoomPinchSens = config.zoomPinchSens;
    if (config.focusDamping !== undefined) this.focusDamping = config.focusDamping;
    if (config.rotateDamping !== undefined) this.rotateDamping = config.rotateDamping;
    if (config.moveDamping !== undefined) this.moveDamping = config.moveDamping;
    if (config.zoomDamping !== undefined) this.zoomDamping = config.zoomDamping;
    if (config.pitchRange) this.pitchRange = config.pitchRange;
    if (config.yawRange) this.yawRange = config.yawRange;
    if (config.zoomRange) this.zoomRange = config.zoomRange;
    if (config.gamepadDeadZone) this.gamepadDeadZone = config.gamepadDeadZone;
    if (config.mobileInputLayout) this.mobileInputLayout = config.mobileInputLayout;
    if (config.invertRotation !== undefined) this.invertRotation = config.invertRotation;
    if (config.orbitRotationLimits) this.setOrbitRotationLimits(config.orbitRotationLimits, true);
  }

  // Enable/disable getters and setters
  set enableFly(enable: boolean) {
    this._enableFly = enable;
    if (!this._enableFly && this._mode === 'fly') {
      this._setMode('orbit');
    }
  }

  get enableFly(): boolean {
    return this._enableFly;
  }

  set enableOrbit(enable: boolean) {
    this._enableOrbit = enable;
    if (!this._enableOrbit && this._mode === 'orbit') {
      this._setMode('fly');
    }
  }

  get enableOrbit(): boolean {
    return this._enableOrbit;
  }

  // Damping getters/setters
  set focusDamping(damping: number) {
    this._focusController.focusDamping = damping;
  }

  get focusDamping(): number {
    return this._focusController.focusDamping;
  }

  set moveDamping(damping: number) {
    this._flyController.moveDamping = damping;
  }

  get moveDamping(): number {
    return this._flyController.moveDamping;
  }

  set rotateDamping(damping: number) {
    this._flyController.rotateDamping = damping;
    this._orbitController.rotateDamping = damping;
  }

  get rotateDamping(): number {
    return this._orbitController.rotateDamping;
  }

  set zoomDamping(damping: number) {
    this._orbitController.zoomDamping = damping;
  }

  get zoomDamping(): number {
    return this._orbitController.zoomDamping;
  }

  // Focus point getter/setter
  set focusPoint(point: pc.Vec3) {
    const position = this.camera.getPosition();
    this._startZoomDist = position.distance(point);
    this._controller.attach(this._pose.look(position, point), false);
  }

  get focusPoint(): pc.Vec3 {
    return this._pose.getFocus(tmpV1);
  }

  // Range getters/setters
  set pitchRange(range: pc.Vec2) {
    this._pitchRange.copy(range);
    this._flyController.pitchRange = this._pitchRange;
    this._orbitController.pitchRange = this._pitchRange;
  }

  get pitchRange(): pc.Vec2 {
    return this._pitchRange;
  }

  set yawRange(range: pc.Vec2) {
    this._yawRange.x = pc.math.clamp(range.x, -360, 360);
    this._yawRange.y = pc.math.clamp(range.y, -360, 360);
    this._flyController.yawRange = this._yawRange;
    this._orbitController.yawRange = this._yawRange;
  }

  get yawRange(): pc.Vec2 {
    return this._yawRange;
  }

  set zoomRange(range: pc.Vec2) {
    this._zoomRange.x = range.x;
    this._zoomRange.y = range.y <= range.x ? Infinity : range.y;
    this._orbitController.zoomRange = this._zoomRange;
  }

  get zoomRange(): pc.Vec2 {
    return this._zoomRange;
  }

  // Mobile input layout
  set mobileInputLayout(layout: MobileInputLayout) {
    if (!/(?:joystick|touch)-(?:joystick|touch)/.test(layout)) {
      console.warn(`CameraControls: invalid mobile input layout: ${layout}`);
      return;
    }
    this._flyMobileInput.layout = layout;
  }

  get mobileInputLayout(): MobileInputLayout {
    return this._flyMobileInput.layout as MobileInputLayout;
  }

  /**
   * Get current camera mode
   */
  get mode(): CameraMode {
    return this._mode;
  }

  /**
   * Set camera mode
   */
  private _setMode(mode: CameraMode, options: { orbitFocusPoint?: pc.Vec3; orbitDistance?: number; snap?: boolean } = {}): void {
    // Override mode depending on enabled features
    if (this._enableFly && !this._enableOrbit) {
      mode = 'fly';
    } else if (!this._enableFly && this._enableOrbit) {
      mode = 'orbit';
    } else if (!this._enableFly && !this._enableOrbit) {
      console.warn('CameraControls: both fly and orbit modes are disabled');
      return;
    }

    // Flush pending inputs so that the mouse click on the mode-toggle button
    // doesn't register as a rotation on the first frame of the new mode.
    this._desktopInput.read();
    this._orbitMobileInput.read();
    this._flyMobileInput.read();
    this._gamepadInput.read();

    const previousMode = this._mode;
    const hasOrbitOverride = mode === 'orbit' && !!options.orbitFocusPoint;
    if (previousMode === mode && !hasOrbitOverride) return;
    this._mode = mode;

    // Cancel any in-progress flyTo tween so it doesn't override the new controller
    this._flyToActive = false;
    this._finishOrbitViewTween();

    // Detach old controller
    if (this._controller) {
      this._controller.detach();
    }

    // Attach new controller
    switch (this._mode) {
      case 'orbit':
        this._controller = this._orbitController;
        if (options.orbitFocusPoint) {
          const position = this.camera.getPosition();
          const focusDistance = typeof options.orbitDistance === 'number' && Number.isFinite(options.orbitDistance) && options.orbitDistance > 0
            ? options.orbitDistance
            : position.distance(options.orbitFocusPoint);
          this._lastFocusPoint.copy(options.orbitFocusPoint);
          this._pose.look(position, options.orbitFocusPoint);
          this._startZoomDist = focusDistance;
          this._pose.distance = focusDistance;
        } else if (previousMode === 'fly') {
          // Coming from fly: preserve current camera orientation, use forward-projected
          // focus point as temporary pivot. createViewer will refine this with a depth pick.
          this.syncFromCamera();
        } else {
          // From focus or other modes: orbit around the last known focus point
          const position = this.camera.getPosition();
          this._pose.look(position, this._lastFocusPoint);
          this._startZoomDist = position.distance(this._lastFocusPoint);
          this._pose.distance = this._startZoomDist;
        }
        // Reset yaw tracking to prevent false discontinuity detection on mode switch
        this._lastYaw = this._pose.angles.y;
        break;
      case 'fly':
        this._controller = this._flyController;
        // Fly mode should always continue from the exact camera pose the user
        // is currently seeing, regardless of the previous sub-mode.
        this.syncFromCamera(undefined, true);
        break;
      case 'focus':
        this._controller = this._focusController;
        break;
    }
    const snap = options.snap === true;
    // PlayCanvas's attach(pose, smooth): false lands the new controller on the pose. (Passing
    // `snap` here used to dampen exactly the callers that asked to snap.)
    this._controller.attach(this._pose, false);

    // Swap mobile input sources so only the active one captures pointer events.
    // Having both MultiTouchSource and DualGestureSource attached simultaneously
    // causes setPointerCapture() conflicts that break pinch-to-zoom in orbit mode.
    if (this._mode === 'orbit' || this._mode === 'focus') {
      const canvas = this.app.graphicsDevice.canvas;
      this._flyMobileInput.detach();
      if (!this._inputsDetached) this._orbitMobileInput.attach(canvas);
    } else if (this._mode === 'fly') {
      const canvas = this.app.graphicsDevice.canvas;
      this._orbitMobileInput.detach();
      if (!this._inputsDetached) this._flyMobileInput.attach(canvas);
    }

    // Skip orbit-rotation-limit clamping when caller is explicitly snapping into
    // orbit. Callers using snap=true (e.g. restoreSavedOrbitView) re-capture the
    // anchor immediately afterward — running the clamp here against the *old*
    // anchor would silently rewrite the pose before the new anchor is set.
    if (!snap && this._mode === 'orbit' && this._applyOrbitRotationLimits()) {
      this._controller.attach(this._pose, false);
      this._applyPoseToCamera();
    }

    // Emit mode change event for UI updates
    this.app.fire('cameracontrols:modechange', this._mode);
  }

  /**
   * Public method to set camera mode (orbit or fly only)
   * Used by UI toggle buttons to switch between orbit and fly modes
   */
  setMode(mode: 'orbit' | 'fly'): void {
    this._setMode(mode);
  }

  /**
   * Switch to orbit around a known focus point and distance without first
   * deriving a temporary pivot from the fly camera pose.
   *
   * Pass snap=true when restoring a saved orbit view: it bypasses the orbit
   * controller's dampening so the camera lands exactly on the supplied pose
   * instead of interpolating from the previous mode's pose.
   */
  setOrbitModeFromFocus(focusPoint: pc.Vec3, distance?: number, snap: boolean = false): void {
    this._setMode('orbit', { orbitFocusPoint: focusPoint, orbitDistance: distance, snap });
  }

  getOrbitState(): { focus: pc.Vec3; distance: number } {
    return {
      focus: this._pose.getFocus(new pc.Vec3()),
      distance: this._pose.distance
    };
  }

  /**
   * Limit orbit rotation relative to an anchor pose. The anchor defaults to the
   * current orbit pose, which should be the initial orbit view when configured.
   */
  setOrbitRotationLimits(limits?: OrbitRotationLimitsConfig | null, resetAnchor: boolean = false): void {
    // Limits set from outside win over the ones an orbit-view move would restore on arrival.
    this._flyToRestoreLimits = null;
    const normalized = this._normalizeOrbitRotationLimits(limits);
    this._orbitRotationLimits = normalized;

    if (!normalized) {
      this._orbitRotationLimitAnchor = null;
      return;
    }

    if (resetAnchor || !this._orbitRotationLimitAnchor) {
      this._captureOrbitRotationLimitAnchor();
    }

    if (this._mode === 'orbit' && this._applyOrbitRotationLimits()) {
      this._controller.attach(this._pose, false);
      this._applyPoseToCamera();
    }
  }

  setOrbitRotationLimitAnchor(position: pc.Vec3, focus: pc.Vec3): void {
    const dir = tmpV1.sub2(focus, position);
    if (dir.lengthSq() < 1e-8) return;

    dir.normalize();
    const elev = Math.atan2(-dir.y, Math.sqrt(dir.x * dir.x + dir.z * dir.z)) * pc.math.RAD_TO_DEG;
    let yaw = Math.atan2(-dir.x, -dir.z) * pc.math.RAD_TO_DEG;
    while (yaw > 180) yaw -= 360;
    while (yaw < -180) yaw += 360;

    this._orbitRotationLimitAnchor = {
      pitch: Math.max(-89, Math.min(89, -elev)),
      yaw,
      focus: focus.clone()
    };
  }

  private _normalizeOrbitRotationLimits(limits?: OrbitRotationLimitsConfig | null): Required<OrbitRotationLimitsConfig> | null {
    if (!limits?.enabled) return null;
    const clampLimit = (value: number | undefined, max: number): number => {
      const numeric = typeof value === 'number' && Number.isFinite(value) ? value : 45;
      return pc.math.clamp(numeric, 0, max);
    };

    return {
      enabled: true,
      up: clampLimit(limits.up, 180),
      down: clampLimit(limits.down, 180),
      left: clampLimit(limits.left, 180),
      right: clampLimit(limits.right, 180)
    };
  }

  private _captureOrbitRotationLimitAnchor(): void {
    const focus = this._pose.getFocus(tmpV1);
    this._lastFocusPoint.copy(focus);
    this._orbitRotationLimitAnchor = {
      pitch: this._pose.angles.x,
      yaw: this._pose.angles.y,
      focus: focus.clone()
    };
  }

  private _rebuildOrbitPoseFromFocus(focus: pc.Vec3): void {
    const focusOffset = tmpV2.set(0, 0, -1);
    tmpQ1.setFromEulerAngles(this._pose.angles).transformVector(focusOffset, focusOffset);
    this._pose.position.copy(focus).sub(focusOffset.mulScalar(this._pose.distance));
  }

  private _applyPoseToCamera(): void {
    this.camera.setPosition(this._pose.position);
    this.camera.setEulerAngles(this._pose.angles);
  }

  private _shortestAngleDeltaDegrees(angle: number, anchor: number): number {
    let delta = angle - anchor;
    while (delta > 180) delta -= 360;
    while (delta < -180) delta += 360;
    return delta;
  }

  private _applyOrbitRotationLimits(): boolean {
    const limits = this._orbitRotationLimits;
    if (this._mode !== 'orbit' || !limits) return false;

    if (!this._orbitRotationLimitAnchor) {
      this._captureOrbitRotationLimitAnchor();
    }
    const anchor = this._orbitRotationLimitAnchor;
    if (!anchor) return false;

    let changed = false;

    const minPitch = anchor.pitch - limits.down;
    const maxPitch = anchor.pitch + limits.up;
    const clampedPitch = pc.math.clamp(this._pose.angles.x, minPitch, maxPitch);
    if (Math.abs(clampedPitch - this._pose.angles.x) > 0.0001) {
      this._pose.angles.x = clampedPitch;
      changed = true;
    }

    const yawDelta = this._shortestAngleDeltaDegrees(this._pose.angles.y, anchor.yaw);
    const clampedYawDelta = pc.math.clamp(yawDelta, -limits.left, limits.right);
    if (Math.abs(clampedYawDelta - yawDelta) > 0.0001) {
      this._pose.angles.y = anchor.yaw + clampedYawDelta;
      this._lastYaw = this._pose.angles.y;
      changed = true;
    }

    if (changed) {
      this._rebuildOrbitPoseFromFocus(anchor.focus);
      this._lastFocusPoint.copy(anchor.focus);
    }

    return changed;
  }

  private _isOrbitPivotLocked(): boolean {
    return this._mode === 'orbit' && this._orbitRotationLimits !== null;
  }

  /**
   * True while orbiting with rotation limits: the pivot stays where the creator set the view, so
   * click-to-refocus and panning are off ("Limit orbit rotation").
   */
  isOrbitPivotLocked(): boolean {
    // Limits lifted for an orbit-view move still lock the pivot: they come back on arrival.
    return this._isOrbitPivotLocked() || (this._mode === 'orbit' && this._flyToRestoreLimits !== null);
  }

  /** True when orbit rotation limits are configured (they lock the pivot whenever orbit is the sub-mode). */
  hasOrbitRotationLimits(): boolean {
    return this._orbitRotationLimits !== null;
  }

  /**
   * Helper: extract current camera angles and compute target look-at angles.
   * Sets _flyToStartAngles and _flyToEndAngles with shortest-arc azimuth normalization.
   */
  private _computeTweenAngles(endPos: pc.Vec3, lookAtPoint: pc.Vec3): void {
    // Current camera angles
    const forward = new pc.Vec3(0, 0, -1);
    this.camera.getRotation().transformVector(forward, forward);
    const curElev = Math.atan2(-forward.y, Math.sqrt(forward.x * forward.x + forward.z * forward.z)) * pc.math.RAD_TO_DEG;
    const curAzim = Math.atan2(-forward.x, -forward.z) * pc.math.RAD_TO_DEG;
    this._flyToStartAngles.set(-curElev, curAzim, 0);

    // Target angles: look from end position toward the target
    const dir = new pc.Vec3().sub2(lookAtPoint, endPos);
    if (dir.lengthSq() < 1e-6) {
      // End position is on top of the target — keep current rotation
      this._flyToEndAngles.set(-curElev, curAzim, 0);
      return;
    }
    dir.normalize();
    const tgtElev = Math.atan2(-dir.y, Math.sqrt(dir.x * dir.x + dir.z * dir.z)) * pc.math.RAD_TO_DEG;
    let tgtAzim = Math.atan2(-dir.x, -dir.z) * pc.math.RAD_TO_DEG;

    // Shortest arc: ensure azimuth delta is within [-180, 180]
    const azimDelta = tgtAzim - curAzim;
    if (azimDelta > 180) tgtAzim -= 360;
    else if (azimDelta < -180) tgtAzim += 360;

    this._flyToEndAngles.set(-tgtElev, tgtAzim, 0);
  }

  private _getFlyToDuration(): number {
    const speed = Number.isFinite(this.autoMoveSpeedFactor)
      ? Math.max(0.1, Math.min(3, this.autoMoveSpeedFactor))
      : 1;
    return 0.6 / speed;
  }

  /**
   * Focus camera on a point with smooth animation.
   * After the focus animation completes, orbit mode will use this point as the orbit center.
   *
   * Uses a manual position/angle tween instead of FocusController.
   * Camera moves to the midpoint and rotates to look at the focus point.
   */
  focus(focusPoint: pc.Vec3, resetZoom: boolean = false): void {
    if (this._isOrbitPivotLocked() || this._orbitViewMoveActive) return;

    this._lastFocusPoint.copy(focusPoint);

    const cameraPos = this.camera.getPosition();
    this._startZoomDist = resetZoom
      ? this._startZoomDist
      : cameraPos.distance(focusPoint);

    // Position: current → midpoint between camera and focus point
    this._flyToStartPos.copy(cameraPos);
    this._flyToEndPos.lerp(cameraPos, focusPoint, 0.5);

    // Rotation: current → looking at focus point from midpoint
    this._computeTweenAngles(this._flyToEndPos, focusPoint);

    this._flyToProgress = 0;
    this._flyToDuration = this._getFlyToDuration();
    this._flyToActive = true;
    this._flyToIsFocus = true;
  }

  /**
   * Move to an orbit view: tween the camera to `position` while turning to face `pivot`, then orbit
   * `pivot` from there. Orbit mode only (returns false otherwise). The distance is kept inside the
   * zoom limits. Rotation limits ("Limit orbit rotation") are lifted for the move and re-anchored
   * to the new view when it ends, so the visitor gets the same degree limits around the new pivot.
   * `onDone` runs once when the move ends, whether it arrives or is interrupted.
   * `instant: true` lands on the next frame instead of travelling.
   */
  animateToOrbitView(position: pc.Vec3, pivot: pc.Vec3, onDone?: () => void, options?: ViewMoveOptions): boolean {
    if (this._mode !== 'orbit') return false;
    const offset = new pc.Vec3().sub2(position, pivot);
    const distance = offset.length();
    if (!Number.isFinite(distance) || distance < 1e-4) return false;
    // Finish a previous orbit-view move first (its limits, its callback).
    this._finishOrbitViewTween();
    const clamped = pc.math.clamp(distance, this._zoomRange.x, this._zoomRange.y);
    const endPos = new pc.Vec3().copy(pivot).add(offset.mulScalar(clamped / distance));

    this._flyToRestoreLimits = this._orbitRotationLimits;
    this._orbitRotationLimits = null;
    this._orbitRotationLimitAnchor = null;
    this._flyToOnDone = onDone ?? null;
    this._orbitViewMoveActive = true;

    this._lastFocusPoint.copy(pivot);
    this._startZoomDist = clamped;
    this._flyToStartPos.copy(this.camera.getPosition());
    this._flyToEndPos.copy(endPos);
    this._computeTweenAngles(endPos, pivot);
    this._flyToProgress = 0;
    this._flyToDuration = this._getViewMoveDuration(options);
    this._flyToActive = true;
    this._flyToIsFocus = true;
    return true;
  }

  /**
   * Fly-mode counterpart of animateToOrbitView: tween the camera to `position` while turning to face
   * `lookAt`, then hand control back to fly mode there (a first-person extra waypoint). Fly mode
   * only (returns false otherwise). `onDone` runs once when the move ends, arrived or interrupted.
   */
  animateToFlyView(position: pc.Vec3, lookAt: pc.Vec3, onDone?: () => void, options?: ViewMoveOptions): boolean {
    if (this._mode !== 'fly') return false;
    const finite = [position.x, position.y, position.z, lookAt.x, lookAt.y, lookAt.z].every(Number.isFinite);
    if (!finite || position.distance(lookAt) < 1e-4) return false;
    this._finishOrbitViewTween();
    this._flyToOnDone = onDone ?? null;
    // Blocks click-to-refocus / fly-to while the move runs, as for an orbit view.
    this._orbitViewMoveActive = true;
    this._lastFocusPoint.copy(lookAt);
    this._flyToStartPos.copy(this.camera.getPosition());
    this._flyToEndPos.copy(position);
    this._computeTweenAngles(position, lookAt);
    this._flyToProgress = 0;
    this._flyToDuration = this._getViewMoveDuration(options);
    this._flyToActive = true;
    this._flyToIsFocus = false;
    return true;
  }

  /** A view move is a little slower than a click-to-refocus (it can cross the whole scene). */
  private _getViewMoveDuration(options?: ViewMoveOptions): number {
    return options?.instant ? INSTANT_VIEW_MOVE_SECONDS : this._getFlyToDuration() * 1.5;
  }

  /** End of an animateToOrbitView move: put the rotation limits back around the new view, notify. */
  private _finishOrbitViewTween(): void {
    const limits = this._flyToRestoreLimits;
    const onDone = this._flyToOnDone;
    this._flyToRestoreLimits = null;
    this._flyToOnDone = null;
    this._orbitViewMoveActive = false;
    if (limits) {
      this._orbitRotationLimits = limits;
      this._captureOrbitRotationLimitAnchor();
    }
    onDone?.();
  }

  /**
   * Fly the camera toward a target point, stopping at the midpoint.
   * Camera smoothly rotates to look at the target during the move.
   */
  flyTo(targetPoint: pc.Vec3, _stopDistance: number = 2): void {
    if (this._orbitViewMoveActive) return;
    this._lastFocusPoint.copy(targetPoint);

    const cameraPos = this.camera.getPosition();
    this._flyToStartPos.copy(cameraPos);

    // Move to midpoint, capped at 50 units to avoid depth-buffer artifact jumps
    const maxTravelDistance = 50;
    const fullDistance = cameraPos.distance(targetPoint);
    const lerpFactor = fullDistance > 0.001
      ? Math.min(0.5, maxTravelDistance / fullDistance)
      : 0;
    this._flyToEndPos.lerp(cameraPos, targetPoint, lerpFactor);

    // If endpoint would land inside a collision mesh, binary-search backward
    // toward the start to find a safe position (prevents camera getting trapped).
    const hasCollision = this._collisionEntities.length > 0 || this._voxelCollision !== null || this._meshCollision !== null;
    if (hasCollision && this.checkCollision(this._flyToEndPos)) {
      const safePos = tmpV1;
      let lo = 0, hi = lerpFactor;
      for (let i = 0; i < 8; i++) {
        const mid = (lo + hi) * 0.5;
        safePos.lerp(cameraPos, targetPoint, mid);
        if (this.checkCollision(safePos)) {
          hi = mid;
        } else {
          lo = mid;
        }
      }
      this._flyToEndPos.lerp(cameraPos, targetPoint, lo);
    }

    // Rotation: current → looking at target from midpoint
    this._computeTweenAngles(this._flyToEndPos, targetPoint);

    this._flyToProgress = 0;
    this._flyToDuration = this._getFlyToDuration();
    this._flyToActive = true;
    this._flyToIsFocus = false;
  }

  /**
   * Look at a point without moving
   */
  look(focusPoint: pc.Vec3, resetZoom: boolean = false): void {
    if (this._mode !== 'focus') {
      this._preFocusMode = this._mode;
    }
    this._setMode('focus');
    const position = resetZoom
      ? tmpV1.copy(this.camera.getPosition()).sub(focusPoint).normalize().mulScalar(this._startZoomDist).add(focusPoint)
      : this.camera.getPosition();
    this._controller.attach(pose.look(position, focusPoint));
  }

  /**
   * Reset camera to a position looking at focus point
   */
  reset(focusPoint: pc.Vec3, position: pc.Vec3): void {
    if (this._mode !== 'focus') {
      this._preFocusMode = this._mode;
    }
    this._setMode('focus');
    this._controller.attach(pose.look(position, focusPoint));
  }

  /**
   * Sync internal pose from camera's current position and rotation.
   * If a target is provided, the pose is set up to look at that target from the current position.
   * Otherwise, preserves the camera's exact rotation.
   *
   * The controller always lands on the synced pose: its dampened "current"
   * state is reset too, so the next update() can't ease the camera back toward
   * wherever it was before the sync (a visible snap-back after every external
   * camera move). `snap` is kept for callers that ask for that explicitly. It
   * used to be passed to PlayCanvas's attach(pose, smooth), where true means
   * smooth, so snap=true callers were the ones being dampened.
   */
  syncFromCamera(target?: pc.Vec3, force: boolean = false, _snap: boolean = false): void {
    if (this._isOrbitPivotLocked() && !force) return;

    const position = this.camera.getPosition().clone();

    // Reset collision tracking so the first frame after sync just records the pose: a stale
    // "previous" pose would otherwise be restored if the synced pose touches collision.
    this._prevPositionValid = false;
    this._prevPoseValid = false;

    if (target) {
      // When we have a target, use pose.look() to properly set up the pose
      // This calculates correct angles to look at the target from current position
      this._lastFocusPoint.copy(target);
      const focusDistance = position.distance(target);
      this._startZoomDist = focusDistance;
      this._pose.distance = focusDistance;

      // Use pose.look() which properly calculates yaw/pitch to face the target
      this._pose.look(position, target);

      // Normalize yaw to [-180, 180] range
      let yaw = this._pose.angles.y;
      while (yaw > 180) yaw -= 360;
      while (yaw < -180) yaw += 360;
      this._pose.angles.y = yaw;
      this._lastYaw = yaw;

      // Clamp pitch to prevent flipping (avoid gimbal lock near ±90°)
      this._pose.angles.x = Math.max(-89, Math.min(89, this._pose.angles.x));

      // Ensure no roll
      this._pose.angles.z = 0;
    } else {
      // No target - preserve camera's exact rotation.
      //
      // Derive angles from the camera's forward vector (the same convention as
      // Pose.look() / syncFromPose) instead of camera.getEulerAngles(). The
      // Euler decomposition is ambiguous near gimbal-lock and can flip the
      // returned angles even when the camera's actual forward direction is
      // fine. The visible symptom of using getEulerAngles() here was: dropping
      // an LCC2 (Z-up source, R_x(-90°) entity-rotation correction), entering
      // orbit, then switching to fly — fly inherited a bogus 90° pitch and the
      // view jumped straight up toward the sky.
      const forward = this.camera.forward.clone();
      const elev = Math.atan2(-forward.y, Math.sqrt(forward.x * forward.x + forward.z * forward.z)) * pc.math.RAD_TO_DEG;
      const azim = Math.atan2(-forward.x, -forward.z) * pc.math.RAD_TO_DEG;

      // Set pose position directly from camera
      this._pose.position.copy(position);

      // Set pose angles using the forward-vector derivation. Pose.look()'s
      // convention is angles.x = -elev (pitch positive = looking down) and
      // angles.y = azim.
      this._pose.angles.x = -elev;
      this._pose.angles.y = azim;
      this._pose.angles.z = 0; // Always zero roll to prevent flipping

      // Normalize yaw to [-180, 180] range
      let yaw = this._pose.angles.y;
      while (yaw > 180) yaw -= 360;
      while (yaw < -180) yaw += 360;
      this._pose.angles.y = yaw;
      this._lastYaw = yaw;

      // Clamp pitch to prevent flipping
      this._pose.angles.x = Math.max(-89, Math.min(89, this._pose.angles.x));

      // Calculate a focus point 10 units in front of the camera
      this._lastFocusPoint.copy(position).add(forward.mulScalar(10));

      const focusDistance = 10;
      this._startZoomDist = focusDistance;
      this._pose.distance = focusDistance;
    }

    // Reattach the current controller with the synced pose (smooth=false: snap)
    this._controller.attach(this._pose, false);
  }

  /**
   * Sync internal pose from explicit position and rotation values.
   * Use this when you need to bypass the camera entity's current state.
   * Also sets the camera entity to match these values.
   */
  syncFromPose(position: pc.Vec3, rotation: pc.Quat, focusTarget?: pc.Vec3): void {
    // Reset collision tracking so the first frame after sync just records the pose
    this._prevPositionValid = false;
    this._prevPoseValid = false;

    // Set the camera entity to match the provided pose
    this.camera.setPosition(position);
    this.camera.setRotation(rotation);

    // Derive angles using the same convention as Pose.look() to avoid
    // getEulerAngles() decomposition mismatches that cause camera flips.
    // Camera forward in PlayCanvas is -Z, so transform (0,0,-1) by the rotation.
    const forward = new pc.Vec3(0, 0, -1);
    rotation.transformVector(forward, forward);
    const elev = Math.atan2(-forward.y, Math.sqrt(forward.x * forward.x + forward.z * forward.z)) * pc.math.RAD_TO_DEG;
    const azim = Math.atan2(-forward.x, -forward.z) * pc.math.RAD_TO_DEG;

    // Set pose position
    this._pose.position.copy(position);

    // Set pose angles matching Pose.look() convention: angles.x = -elev, angles.y = azim
    this._pose.angles.x = -elev;
    this._pose.angles.y = azim;
    this._pose.angles.z = 0; // Always zero roll

    // Normalize yaw to [-180, 180] range
    let yaw = this._pose.angles.y;
    while (yaw > 180) yaw -= 360;
    while (yaw < -180) yaw += 360;
    this._pose.angles.y = yaw;
    this._lastYaw = yaw;

    // Clamp pitch to prevent flipping
    this._pose.angles.x = Math.max(-89, Math.min(89, this._pose.angles.x));

    // Calculate focus point
    if (focusTarget) {
      this._lastFocusPoint.copy(focusTarget);
    } else {
      // Calculate a focus point 10 units in front
      const fwd = this.camera.forward.clone();
      this._lastFocusPoint.copy(position).add(fwd.mulScalar(10));
    }

    const focusDistance = position.distance(this._lastFocusPoint);
    this._startZoomDist = focusDistance;
    this._pose.distance = focusDistance;

    // Reattach the current controller with the synced pose
    this._controller.attach(this._pose, false);
  }

  /**
   * Update the orbit focus point and distance without changing camera orientation.
   * Use this to improve orbit behaviour after switching modes.
   */
  setFocusDistance(focusPoint: pc.Vec3, distance: number, force: boolean = false): void {
    if (this._isOrbitPivotLocked() && !force) return;

    this._lastFocusPoint.copy(focusPoint);
    this._startZoomDist = distance;
    this._pose.distance = distance;
  }

  /**
   * Enable controls
   */
  enable(): void {
    // Discard any inputs that accumulated while disabled (e.g. during gizmo drag)
    // to prevent camera jumps on re-enable
    this._desktopInput.read();
    this._orbitMobileInput.read();
    this._flyMobileInput.read();
    this._gamepadInput.read();
    this.enabled = true;
    // Re-attach input sources if they were detached
    if (this._inputsDetached) {
      const canvas = this.app.graphicsDevice.canvas;
      this._desktopInput.attach(canvas);
      // Only attach the mobile source matching the current mode.
      // Detach the OTHER source first — reattachMobileInput() may have left
      // _flyMobileInput attached during walk mode, and if _mode is 'orbit' we
      // must detach it to prevent both sources fighting over setPointerCapture().
      if (this._mode === 'fly') {
        this._orbitMobileInput.detach();
        this._flyMobileInput.attach(canvas);
      } else {
        this._flyMobileInput.detach();
        this._orbitMobileInput.attach(canvas);
      }
      this._gamepadInput.attach(canvas);
      this._inputsDetached = false;
    }
  }

  /**
   * Disable controls
   */
  disable(): void {
    this.enabled = false;
    this.autoMoveForward = false;
    // Discard any pending inputs
    this._desktopInput.read();
    this._orbitMobileInput.read();
    this._flyMobileInput.read();
    this._gamepadInput.read();
  }

  /**
   * Fully detach input sources from the canvas.
   * Use when another system (e.g. CharacterController) needs exclusive pointer access.
   */
  detachInputSources(): void {
    this._desktopInput.detach();
    this._orbitMobileInput.detach();
    this._flyMobileInput.detach();
    this._gamepadInput.detach();
    this._inputsDetached = true;
  }

  private _discardDesktopKeyboardState(): void {
    this._state.axis.set(0, 0, 0);
    this._state.shift = 0;
    this._state.ctrl = 0;

    const keyNow = Object.getOwnPropertyDescriptor(this._desktopInput, '_keyNow')?.value;
    const keyPrev = Object.getOwnPropertyDescriptor(this._desktopInput, '_keyPrev')?.value;
    if (Array.isArray(keyNow)) keyNow.fill(0);
    if (Array.isArray(keyPrev)) keyPrev.fill(0);
  }

  /**
   * Re-attach only the mobile dual-gesture input source without enabling CameraControls.
   * Used for walk mode on mobile where we need joystick events but not CameraControls update().
   * The desktop/orbit/gamepad sources stay detached so CharacterController keeps pointer access.
   */
  /**
   * Walk-mode helper: drain the accumulated touch-drag deltas (screen px since the last call) for the
   * WHOLE screen. Safe to call while CameraControls is disabled (walk mode owns the camera via
   * CharacterController, so CameraControls' own update() never drains this). With the 'touch-touch'
   * layout BOTH halves are raw touches, so we sum leftInput + rightInput — the entire screen is
   * drag-to-look. The joystick DOM widget captures its own pointer, so movement touches never reach
   * this source and are excluded from the look delta.
   */
  readWalkLookDelta(): { x: number; y: number } {
    const { leftInput, rightInput } = this._flyMobileInput.read();
    return {
      x: (leftInput[0] ?? 0) + (rightInput[0] ?? 0),
      y: (leftInput[1] ?? 0) + (rightInput[1] ?? 0),
    };
  }

  // Walk-style mobile fly: the on-screen joystick widget drives fly movement (set here each pointermove,
  // continuous while held, 0 on release), while both screen halves drive look. Consumed by the fly
  // move branch only when mobileInputLayout === 'touch-touch'. Clamped to the -1..1 joystick range.
  private _flyJoystickMove: [number, number] = [0, 0];
  setFlyJoystickMove(x: number, y: number): void {
    this._flyJoystickMove[0] = Math.max(-1, Math.min(1, x));
    this._flyJoystickMove[1] = Math.max(-1, Math.min(1, y));
  }

  reattachMobileInput(): void {
    if (!this._inputsDetached) return;
    const canvas = this.app.graphicsDevice.canvas;
    this._flyMobileInput.attach(canvas);
    // _inputsDetached stays true — enable() will still re-attach remaining sources
  }

  /**
   * Update camera controls - call this every frame
   */
  update(dt: number): void {
    if (!this.enabled) return;

    const { keyCode } = pc.KeyboardMouseSource;
    const editableElementFocused = typeof document !== 'undefined' && isEditableElement(document.activeElement);
    const { key, button, mouse, wheel } = this._desktopInput.read();
    const { touch, pinch, count } = this._orbitMobileInput.read();
    const { leftInput, rightInput } = this._flyMobileInput.read();
    const { leftStick, rightStick } = this._gamepadInput.read();

    // Apply dead zone to gamepad sticks
    applyDeadZone(leftStick, this.gamepadDeadZone.x, this.gamepadDeadZone.y);
    applyDeadZone(rightStick, this.gamepadDeadZone.x, this.gamepadDeadZone.y);

    // Update state. KeyboardMouseSource listens on window, so it sees keys typed
    // into editor/menu inputs. Clear both our accumulator and the source key
    // state while editable UI has focus so typing cannot move or stick the camera.
    if (editableElementFocused) {
      this._discardDesktopKeyboardState();
    } else {
      this._state.axis.add(tmpV1.set(
        (key[keyCode.D] - key[keyCode.A]) + (key[keyCode.RIGHT] - key[keyCode.LEFT]),
        (key[keyCode.E] - key[keyCode.Q]),
        (key[keyCode.W] - key[keyCode.S]) + (key[keyCode.UP] - key[keyCode.DOWN])
      ));
      this._state.shift += key[keyCode.SHIFT];
      this._state.ctrl += key[keyCode.CTRL];
    }

    for (let i = 0; i < this._state.mouse.length; i++) {
      this._state.mouse[i] += button[i];
    }
    this._state.touches += count[0];

    // No automatic mode switching - user controls mode via UI buttons only

    const orbit = +(this._mode === 'orbit');
    const fly = +(this._mode === 'fly');
    const double = +(this._state.touches > 1);
    const desktopPan = +(this._state.shift || this._state.mouse[1]);
    const orbitPan = orbit * +(this.enablePan && !this._isOrbitPivotLocked());
    const mobileJoystick = +(this._flyMobileInput.layout.endsWith('joystick'));

    // Multipliers
    const moveMult = (this._state.shift ? this.moveFastSpeed : this._state.ctrl
      ? this.moveSlowSpeed : this.moveSpeed) * dt;
    const zoomMult = this.zoomSpeed * 60 * dt;
    const zoomTouchMult = zoomMult * this.zoomPinchSens;
    const rotateMult = this.rotateSpeed * 60 * dt;
    const rotateTouchMult = rotateMult * this.rotateTouchSens;
    const rotateJoystickMult = this.rotateSpeed * this.rotateJoystickSens * 60 * dt;

    const { deltas } = frame;

    // Desktop move
    const v = tmpV1.set(0, 0, 0);
    const keyMove = tmpV3.copy(this._state.axis).normalize();
    v.add(keyMove.mulScalar(fly * moveMult * this.keyboardSpeedMultiplier));
    const panMove = screenToWorld(this.cameraComponent, mouse[0], mouse[1], this._pose.distance, tmpV3);
    v.add(panMove.mulScalar(orbitPan * desktopPan));
    const wheelMove = tmpV2.set(0, 0, wheel[0]);
    v.add(wheelMove.mulScalar(orbit * zoomMult));
    deltas.move.append([v.x, v.y, v.z]);

    // Desktop rotate
    v.set(0, 0, 0);
    const mouseRotate = tmpV2.set(mouse[0], mouse[1], 0);
    v.add(mouseRotate.mulScalar((1 - (orbit * desktopPan)) * rotateMult));
    // Invert Drag Direction: flip BOTH axes so the scene follows the pointer (not just the pitch).
    applyLookInversion(v, this.invertRotation);
    deltas.rotate.append([v.x, v.y, v.z]);

    // Mobile move. In walk-style 'touch-touch' fly (mobile explore), the on-screen joystick widget
    // drives movement (both screen halves are look); otherwise the left touch/joystick input drives it.
    const walkStyleFly = this.mobileInputLayout === 'touch-touch';
    v.set(0, 0, 0);
    const flyMove = walkStyleFly
        // The widget's Y is already up-positive (forward), matching walk's setJoystickMove convention;
        // don't negate it again like the down-positive leftInput path, or forward/back inverts.
        ? tmpV2.set(this._flyJoystickMove[0], 0, this._flyJoystickMove[1])
        : tmpV2.set(leftInput[0], 0, -leftInput[1]);
    v.add(flyMove.mulScalar(fly * moveMult));
    const orbitMove = screenToWorld(this.cameraComponent, touch[0], touch[1], this._pose.distance, tmpV4);
    v.add(orbitMove.mulScalar(orbitPan * double));
    const pinchMove = tmpV2.set(0, 0, pinch[0]);
    v.add(pinchMove.mulScalar(orbit * double * zoomTouchMult));
    deltas.move.append([v.x, v.y, v.z]);

    // Mobile rotate (uses rotateTouchMult for reduced sensitivity on touch devices). In walk-style
    // 'touch-touch' fly, look comes from the WHOLE screen (both halves summed); otherwise right only.
    v.set(0, 0, 0);
    const orbitRotate = tmpV2.set(touch[0], touch[1], 0);
    v.add(orbitRotate.mulScalar(orbit * (1 - double) * rotateTouchMult));
    const flyRotate = walkStyleFly
        ? tmpV2.set(leftInput[0] + rightInput[0], leftInput[1] + rightInput[1], 0)
        : tmpV2.set(rightInput[0], rightInput[1], 0);
    v.add(flyRotate.mulScalar(fly * (mobileJoystick ? rotateJoystickMult : rotateTouchMult)));
    // Touch drags honor the same both-axes inversion as the mouse.
    applyLookInversion(v, this.invertRotation);
    deltas.rotate.append([v.x, v.y, v.z]);

    // Gamepad move. PlayCanvas's GamepadSource only exposes the two sticks, so
    // read the raw pad for triggers/bumpers to add vertical thrust and a
    // fast/slow speed modifier — giving a controller full 6-DOF fly control
    // (left stick = planar move, triggers = up/down, bumpers = speed).
    const rawPad = readGamepad(Math.min(this.gamepadDeadZone.x, this.gamepadDeadZone.y));
    let gpSpeedFactor = 1;
    if (rawPad.connected) {
      if (rawPad.buttons[GP_BUTTON.RB]) gpSpeedFactor = this.moveFastSpeed / this.moveSpeed;
      else if (rawPad.buttons[GP_BUTTON.LB]) gpSpeedFactor = this.moveSlowSpeed / this.moveSpeed;
    }
    const gpMoveMult = moveMult * gpSpeedFactor;
    v.set(0, 0, 0);
    // Right trigger climbs, left trigger descends (analog).
    const gpVertical = rawPad.connected ? (rawPad.rt - rawPad.lt) : 0;
    const stickMove = tmpV2.set(leftStick[0], gpVertical, -leftStick[1]);
    v.add(stickMove.mulScalar(fly * gpMoveMult));
    deltas.move.append([v.x, v.y, v.z]);

    // Cancel auto-move if the user is rotating the camera
    if (this.autoMoveForward && fly && deltas.rotate.length() > 0.001) {
      this.autoMoveForward = false;
    }

    // Auto-move forward: inject continuous forward movement when active in fly mode
    if (this.autoMoveForward && fly) {
      const autoMoveMult = this.moveSpeed * this.autoMoveSpeedFactor * dt;
      deltas.move.append([0, 0, autoMoveMult]);
    }

    // Gamepad rotate
    v.set(0, 0, 0);
    const stickRotate = tmpV2.set(rightStick[0], rightStick[1], 0);
    v.add(stickRotate.mulScalar(fly * rotateJoystickMult));
    // A stick is not a drag: keep the conventional gamepad invert-Y (pitch only) under the toggle.
    if (this.invertRotation) v.y = -v.y;
    deltas.rotate.append([v.x, v.y, v.z]);

    // Check if XR is active - discard frame if so
    if ((this.app as AppWithXR).xr?.active) {
      frame.read();
      return;
    }

    // Check focus end - return to previous mode (orbit or fly)
    if (this._mode === 'focus') {
      const focusInterrupt = deltas.move.length() + deltas.rotate.length() > 0;
      const focusComplete = (this._focusController as FocusControllerWithComplete).complete?.() ?? false;
      if (focusInterrupt || focusComplete) {
        this._setMode(this._preFocusMode);
      }
    }

    // Update controller by consuming frame
    this._pose.copy(this._controller.update(frame, dt));

    // Collision detection.
    // Fly mode: axis-separated clamp so the camera slides along walls.
    // Orbit mode: reject the rotation/pan/zoom whole-cloth and roll back to the
    // last non-colliding pose. Clamping only the position and letting the
    // controller re-attach drifts the orbit target toward the camera's
    // look direction (POV-like behavior).
    const hasCollision = this._collisionEntities.length > 0 || this._voxelCollision !== null || this._meshCollision !== null;
    if (this._mode === 'fly' && hasCollision) {
      if (this._prevPositionValid) {
        // Re-sync controller to corrected position to prevent internal state drift
        if (this.clampFlyStepToCollision(this._pose.position, this._prevPosition)) {
          this._controller.attach(this._pose, false);
        }
      }
      this._prevPosition.copy(this._pose.position);
      this._prevPositionValid = true;
    } else if (this._mode === 'orbit' && hasCollision) {
      if (this.shouldRevertOrbitPose()) {
        // Rotation/pan/zoom would push the camera into a wall — revert the
        // whole pose (position + angles + distance) and reset the controller
        // so the orbit target stays put.
        this._pose.copy(this._prevPose);
        this._controller.attach(this._pose, true);
      } else {
        this._prevPose.copy(this._pose);
        this._prevPoseValid = true;
      }
      this._prevPositionValid = false;
    } else {
      // Reset tracking when not in fly/orbit mode so first frame initializes correctly
      this._prevPositionValid = false;
      this._prevPoseValid = false;
    }

    // Fix yaw discontinuity in orbit mode
    // PlayCanvas's Pose.lerp() uses `% 360` after lerpAngle which doesn't handle
    // negative angles correctly (e.g., -185 % 360 = -185, not 175)
    // This causes sudden jumps when yaw crosses the ±180° boundary
    if (this._mode === 'orbit') {
      // Normalize yaw to [-180, 180) range to prevent accumulation issues
      let yaw = this._pose.angles.y;
      while (yaw > 180) yaw -= 360;
      while (yaw < -180) yaw += 360;

      if (this._lastYaw !== undefined) {
        // Check for discontinuous jump (more than 90° in a single frame is unnatural)
        const yawDelta = yaw - this._lastYaw;

        // If there's a large jump, adjust to maintain continuity
        if (yawDelta > 180) {
          yaw -= 360;
        } else if (yawDelta < -180) {
          yaw += 360;
        }
      }

      this._pose.angles.y = yaw;
      this._lastYaw = yaw;
    }

    // Manual fly-to animation: overrides pose with a straight-line tween
    if (this._flyToActive) {
      // Cancel on any user input (mouse/keyboard/touch movement)
      const hasInput = deltas.move.length() + deltas.rotate.length() > 0;
      if (hasInput) {
        this._flyToActive = false;
        // Sync pose to wherever we got so far.
        // For focus tweens, use the focus point as the orbit target
        // so orbit continues around the intended focus point.
        if (this._flyToIsFocus) {
          this.syncFromCamera(this._lastFocusPoint);
        } else {
          this.syncFromCamera();
        }
        this._finishOrbitViewTween();
      } else {
        this._flyToProgress = Math.min(1, this._flyToProgress + dt / this._flyToDuration);
        // Ease-out cubic for smooth deceleration
        const t = 1 - Math.pow(1 - this._flyToProgress, 3);
        this._pose.position.lerp(this._flyToStartPos, this._flyToEndPos, t);
        this._pose.angles.x = pc.math.lerpAngle(this._flyToStartAngles.x, this._flyToEndAngles.x, t);
        this._pose.angles.y = pc.math.lerpAngle(this._flyToStartAngles.y, this._flyToEndAngles.y, t);
        this._pose.angles.z = 0;
        if (this._flyToProgress >= 1) {
          this._flyToActive = false;
          this._prevPositionValid = false;

          if (this._flyToIsFocus) {
            // Set up orbit around the focus point — use actual distance from end position
            const dist = this._pose.position.distance(this._lastFocusPoint);
            this._pose.look(this._pose.position, this._lastFocusPoint);
            this._pose.distance = dist;
            this._startZoomDist = dist;
            // Sync yaw tracking to prevent false discontinuity detection
            this._lastYaw = this._pose.angles.y;
          }

          // Re-attach controller from the final pose directly.
          // Cannot use syncFromCamera() here because the camera entity
          // hasn't been updated yet (that happens after this block),
          // so it would read stale position and cause a jump.
          this._controller.attach(this._pose, false);
          this._finishOrbitViewTween();
        }
      }
    }

    if (this._applyOrbitRotationLimits()) {
      // Snap (not soft-attach) so the controller's internal current AND target
      // both pin to the clamped pose. Soft-attach only updates the target, so
      // while the user keeps dragging into the boundary the controller's
      // dampened current state and input accumulator keep trying to escape past
      // the limit — producing visible jitter as the clamp pulls them back each
      // frame. Snapping prevents the accumulator from drifting past the clamp.
      this._controller.attach(this._pose, true);
    }

    this._applyPoseToCamera();
  }

  /**
   * Set collision mesh entities for explore mode collision detection.
   * Prevents the camera from passing through collision meshes in fly and orbit modes.
   */
  setCollisionEntities(entities: pc.Entity[], radius?: number): void {
    this._collisionEntities = entities;
    if (radius !== undefined) this._collisionRadius = radius;
    this._prevPositionValid = false;
  }

  /**
   * Set voxel octree collision for fly/orbit mode collision detection.
   * The splat entity is needed to transform camera positions from world space to file space.
   */
  setVoxelCollision(voxel: VoxelCollision | null, splatEntity: pc.Entity | null): void {
    this._voxelCollision = voxel;
    this._voxelSplatEntity = splatEntity;
    this._prevPositionValid = false;
  }

  /**
   * Set triangle-mesh collision (parallel to voxel) for fly/orbit mode.
   * The splat entity is reused for the world↔file transform; passing it again
   * keeps the API symmetrical with setVoxelCollision.
   */
  setMeshCollision(mesh: import('./MeshCollision').MeshCollision | null, splatEntity: pc.Entity | null): void {
    this._meshCollision = mesh;
    if (splatEntity) this._voxelSplatEntity = splatEntity;
    this._prevPositionValid = false;
  }

  /** Switch which splat-derived representation fly/orbit collision queries against. */
  setCollisionSystem(system: 'voxel' | 'mesh'): void {
    if (this._collisionSystem === system) return;
    this._collisionSystem = system;
    this._prevPositionValid = false;
  }

  /** Enable / disable the splat-derived collision query path (voxel + mesh). */
  setSplatCollisionEnabled(enabled: boolean): void {
    if (this._splatCollisionEnabled === enabled) return;
    this._splatCollisionEnabled = enabled;
    this._prevPositionValid = false;
  }

  /**
   * Fly-mode collision for one step from `prev` to `newPos` (corrected in place): each axis that
   * would move the camera into collision is reverted, so it slides along walls. A camera that
   * already starts inside collision (a waypoint, an orbit-to-fly switch or a splat swap put it
   * there, or a voxel set generated with stray voxels) is never held: every axis test would
   * collide and it could not move at all, so it moves freely until it is out. Returns whether
   * anything was corrected.
   *
   * Each axis move is tested along its length, at most one collision radius apart: a long frame (a
   * hitch, up to the engine's 0.1 s cap) can move the camera more than twice the radius, clean
   * through a thin surface (a model's zero-thickness ground or wall) between two tested points.
   */
  private clampFlyStepToCollision(newPos: pc.Vec3, prev: pc.Vec3): boolean {
    const test = this._collisionTestVec;
    const spacing = Math.max(0.05, this._collisionRadius);
    let startsInside: boolean | null = null;
    const blocked = (fx: number, fy: number, fz: number, x: number, y: number, z: number): boolean => {
      const steps = Math.max(1, Math.ceil(Math.hypot(x - fx, y - fy, z - fz) / spacing) || 1);
      let hit = false;
      for (let i = 1; i <= steps && !hit; i++) {
        const t = i / steps;
        hit = this.checkCollision(test.set(fx + (x - fx) * t, fy + (y - fy) * t, fz + (z - fz) * t));
      }
      if (!hit) return false;
      // Checked once, and only when a move is about to be reverted.
      if (startsInside === null) startsInside = this.checkCollision(test.copy(prev));
      return !startsInside;
    };
    let corrected = false;
    // Test X axis independently
    if (blocked(prev.x, prev.y, prev.z, newPos.x, prev.y, prev.z)) {
      newPos.x = prev.x;
      corrected = true;
    }
    // Test Y axis independently
    if (blocked(newPos.x, prev.y, prev.z, newPos.x, newPos.y, prev.z)) {
      newPos.y = prev.y;
      corrected = true;
    }
    // Test Z axis independently
    if (blocked(newPos.x, newPos.y, prev.z, newPos.x, newPos.y, newPos.z)) {
      newPos.z = prev.z;
      corrected = true;
    }
    return corrected;
  }

  /**
   * Orbit mode: whether this frame's pose must roll back to the last pose because it collides.
   * Only a rollback to a pose that was itself clear: a camera whose last pose already collided
   * (it started inside collision) would otherwise be frozen there, unable to rotate or zoom out.
   */
  private shouldRevertOrbitPose(): boolean {
    return this._prevPoseValid
      && this.checkCollision(this._pose.position)
      && !this.checkCollision(this._prevPose.position);
  }

  /**
   * Check if a position collides with any collision mesh (AABB check) or voxel octree.
   * Uses the same approach as CharacterController for consistency.
   */
  private checkCollision(position: pc.Vec3): boolean {
    const r = this._collisionRadius;

    // Check primitive collision meshes (AABB)
    for (const entity of this._collisionEntities) {
      // A model used as collision (an uploaded GLB) is tested against its real triangles. Its
      // bounding box would hold the camera inside an environment mesh — counted as "already
      // inside", so the camera flew through every wall and floor — and be an invisible wall
      // around it from outside.
      const triangles = triangleColliderOf(entity);
      if (triangles) {
        if (triangles.checkSphereSolid(position.x, position.y, position.z, r)) return true;
        continue;
      }

      // World-space box: precomputed for scene colliders, from the live transform (rotation
      // included) for editor ones, so both collide alike.
      const box = colliderBox(entity, this._collisionBox);
      const dx = Math.abs(position.x - box.cx);
      const dy = Math.abs(position.y - box.cy);
      const dz = Math.abs(position.z - box.cz);

      if (dx < box.hx + r && dy < box.hy + r && dz < box.hz + r) {
        return true;
      }
    }

    // Splat-derived collision: query the active representation (voxel or mesh).
    // Mesh data is pre-baked into world space by CharacterController so we query
    // it directly. Voxel data lives in file space, so we round-trip through the
    // splat entity's inverse world matrix — the position AND the camera radius,
    // which is world metres (the same conservative conversion as walk mode), so the
    // camera's collision size doesn't grow or shrink with the splat's scale.
    const active: { checkSphereSolid: (x: number, y: number, z: number, r: number) => boolean } | null =
      !this._splatCollisionEnabled
        ? null
        : this._collisionSystem === 'mesh' && this._meshCollision
          ? this._meshCollision
          : this._collisionSystem === 'voxel' && this._voxelCollision
            ? this._voxelCollision
            // Fallback to whichever is loaded if the preferred one isn't available.
            : (this._voxelCollision ?? this._meshCollision);

    if (active) {
      const isMesh = this._meshCollision !== null && active === this._meshCollision;
      if (isMesh) {
        if (active.checkSphereSolid(position.x, position.y, position.z, r)) {
          return true;
        }
      } else if (this._voxelSplatEntity) {
        const filePos = this._voxelFilePos;
        this._voxelSplatMatrix.copy(this._voxelSplatEntity.getWorldTransform());
        this._voxelInvSplatMatrix.copy(this._voxelSplatMatrix).invert();
        this._voxelInvSplatMatrix.transformPoint(position, filePos);
        const fileRadius = r * fileUnitsPerWorldUnit(this._voxelInvSplatMatrix.data);

        if (active.checkSphereSolid(filePos.x, filePos.y, filePos.z, fileRadius)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this._destroyed) return;
    this._destroyed = true;
    this._desktopInput.destroy();
    this._orbitMobileInput.destroy();
    this._flyMobileInput.destroy();
    this._gamepadInput.destroy();

    this._flyController.destroy();
    this._orbitController.destroy();

    this._voxelCollision = null;
    this._meshCollision = null;
  }
}
